import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../../../context/FirebaseContext';
import {
  getCallHistory,
  getCallHistoryEntry,
  getCallHistoryStats,
  getCallerTypeDisplayInfo,
  getValidationStatusDisplayInfo,
} from '../../../api/conceptgate';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ComputerIcon from '@mui/icons-material/Computer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WebhookIcon from '@mui/icons-material/Webhook';
import ValidationCallHistoryModal from './ValidationCallHistoryModal';
import './ValidationCallHistory.css';

const CallerTypeIcon = ({ callerType }) => {
  switch (callerType) {
    case 'UI':
      return <ComputerIcon fontSize="small" />;
    case 'MCP':
      return <SmartToyIcon fontSize="small" />;
    case 'WEBHOOK':
      return <WebhookIcon fontSize="small" />;
    default:
      return <HistoryIcon fontSize="small" />;
  }
};

const ValidationCallHistory = () => {
  const { auth } = useFirebase();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [filterType, setFilterType] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await getCallHistory(auth, (result) => {
        if (result.error) {
          setError(result.error);
        } else {
          setHistory(result || []);
        }
      }, { limit: 50, callerType: filterType });

      await getCallHistoryStats(auth, (result) => {
        if (!result.error) {
          setStats(result || []);
        }
      }, 7);
    } catch (e) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [auth, filterType]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleOpenDetails = async (entry) => {
    setSelectedEntry(entry);
    setSelectedDetails(null);
    setModalLoading(true);

    await getCallHistoryEntry(auth, (result) => {
      setModalLoading(false);
      if (!result.error) {
        setSelectedDetails(result);
      }
    }, entry.id);
  };

  const handleCloseModal = () => {
    setSelectedEntry(null);
    setSelectedDetails(null);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="validation-call-history compact">
      <div className="vch-header">
        <HistoryIcon className="vch-icon" fontSize="small" />
        <span className="vch-title">API Calls</span>
        <div className="vch-stat-breakdown">
          {stats.map((stat) => {
            const info = getCallerTypeDisplayInfo(stat.callerType);
            return (
              <button
                key={stat.callerType}
                className={`vch-stat-chip ${filterType === stat.callerType ? 'active' : ''}`}
                style={{ '--chip-color': info.color }}
                onClick={() => setFilterType(filterType === stat.callerType ? null : stat.callerType)}
                title={`${info.label}: ${stat.count} calls`}
              >
                <CallerTypeIcon callerType={stat.callerType} />
                <span>{stat.count}</span>
              </button>
            );
          })}
        </div>
        <button
          className="vch-refresh-btn"
          onClick={loadHistory}
          disabled={loading}
          title="Refresh history"
        >
          <RefreshIcon fontSize="small" className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {error && <div className="vch-error">{error}</div>}

      {loading && history.length === 0 ? (
        <div className="vch-loading">Loading history...</div>
      ) : history.length === 0 ? (
        <div className="vch-empty">
          <p>No API calls in the last 7 days</p>
          <p className="vch-empty-hint">
            Validation calls from Web UI, MCP agents, and webhooks will appear here.
          </p>
        </div>
      ) : (
        <div className="vch-list">
          {history.map((entry) => {
            const callerInfo = getCallerTypeDisplayInfo(entry.callerType);
            const statusInfo = getValidationStatusDisplayInfo(entry.responseStatus);

            return (
              <div
                key={entry.id}
                className="vch-entry"
                onClick={() => handleOpenDetails(entry)}
              >
                <div className="vch-entry-header">
                  <div className="vch-entry-caller" style={{ color: callerInfo.color }} title={callerInfo.label}>
                    <CallerTypeIcon callerType={entry.callerType} />
                  </div>
                  <div className="vch-entry-instrument">
                    {entry.instrument || '-'}
                    {entry.direction && <span className="vch-direction">{entry.direction}</span>}
                    {entry.callerType === 'WEBHOOK' && entry.pipelineName && (
                      <span className="vch-pipeline-name" title={entry.pipelineName}>
                        [{entry.pipelineName}]
                      </span>
                    )}
                  </div>
                  <div
                    className="vch-entry-status"
                    style={{ color: statusInfo?.color || '#95a5a6' }}
                  >
                    {entry.responseStatus === 'GO' ? '✓' : entry.responseStatus === 'NO_GO' ? '✗' : entry.responseStatus?.charAt(0) || '?'}
                  </div>
                  <div className="vch-entry-meta">
                    <span className="vch-entry-duration">{entry.durationMs ? `${entry.durationMs}ms` : ''}</span>
                    <span className="vch-entry-time">{formatTimestamp(entry.createdAt)}</span>
                  </div>
                  <div className="vch-entry-expand">
                    <OpenInNewIcon fontSize="small" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Details Modal */}
      {selectedEntry && (
        <ValidationCallHistoryModal
          entry={selectedEntry}
          details={modalLoading ? null : selectedDetails}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ValidationCallHistory;
